import { create } from "zustand";
import type { Task, TemplateTask } from "@/types/task";
import type { ArcgisLayerConfig } from "@/types/layers";

interface ModalStore {
  // Task-related modals (MapPage)
  showCreateTask: boolean;
  editingTask: Task | null;
  viewingTask: Task | null;
  showGeometrySelection: boolean;

  // Calendar and dropdowns (TaskTable)
  isCalendarOpen: boolean;
  isUserDropdownOpen: boolean;
  isTeamDropdownOpen: boolean;

  // Navigation modals (TopNavbar)
  isNavDropdownOpen: boolean;
  showAboutModal: boolean;

  // Template modals (TemplateStore integration)
  showCreateTemplate: boolean;
  editingTemplate: TemplateTask | null;
  showStepsModal: boolean;
  showTemplateSteps: boolean;

  // Layer modals (LayerStore integration)
  showCreateLayer: boolean;
  editingLayer: ArcgisLayerConfig | null;

  // Actions to open specific modals (closes all others)
  openCreateTask: () => void;
  openEditTask: (task: Task) => void;
  openViewTask: (task: Task) => void;
  openGeometrySelection: () => void;
  openCalendar: () => void;
  openUserDropdown: () => void;
  openTeamDropdown: () => void;
  openNavDropdown: () => void;
  openAboutModal: () => void;
  openCreateTemplate: () => void;
  openEditTemplate: (template: TemplateTask) => void;
  openStepsModal: () => void;
  openTemplateSteps: () => void;
  openCreateLayer: () => void;
  openEditLayer: (layer: ArcgisLayerConfig) => void;

  // Close specific modals
  closeCreateTask: () => void;
  closeEditTask: () => void;
  closeViewTask: () => void;
  closeGeometrySelection: () => void;
  closeCalendar: () => void;
  closeUserDropdown: () => void;
  closeTeamDropdown: () => void;
  closeNavDropdown: () => void;
  closeAboutModal: () => void;
  closeCreateTemplate: () => void;
  closeEditTemplate: () => void;
  closeStepsModal: () => void;
  closeTemplateSteps: () => void;
  closeCreateLayer: () => void;
  closeEditLayer: () => void;

  // Close all modals
  closeAllModals: () => void;

  // Toggle actions
  toggleCalendar: () => void;
  toggleUserDropdown: () => void;
  toggleTeamDropdown: () => void;
  toggleNavDropdown: () => void;
}

export const useModalStore = create<ModalStore>((set, get) => ({
  // Initial state - all modals closed
  showCreateTask: false,
  editingTask: null,
  viewingTask: null,
  showGeometrySelection: false,
  isCalendarOpen: false,
  isUserDropdownOpen: false,
  isTeamDropdownOpen: false,
  isNavDropdownOpen: false,
  showAboutModal: false,
  showCreateTemplate: false,
  editingTemplate: null,
  showStepsModal: false,
  showTemplateSteps: false,
  showCreateLayer: false,
  editingLayer: null,

  // Helper function to close all modals
  closeAllModals: () =>
    set({
      showCreateTask: false,
      editingTask: null,
      viewingTask: null,
      showGeometrySelection: false,
      isCalendarOpen: false,
      isUserDropdownOpen: false,
      isTeamDropdownOpen: false,
      isNavDropdownOpen: false,
      showAboutModal: false,
      showCreateTemplate: false,
      editingTemplate: null,
      showStepsModal: false,
      showTemplateSteps: false,
      showCreateLayer: false,
      editingLayer: null,
    }),

  // Open actions (close all others first)
  openCreateTask: () => {
    get().closeAllModals();
    set({ showCreateTask: true, editingTask: null, viewingTask: null });
  },

  openEditTask: (task: Task) => {
    get().closeAllModals();
    set({ editingTask: task });
  },

  openViewTask: (task: Task) => {
    get().closeAllModals();
    set({ viewingTask: task });
  },

  openGeometrySelection: () => {
    get().closeAllModals();
    set({ showGeometrySelection: true });
  },

  openCalendar: () => {
    get().closeAllModals();
    set({ isCalendarOpen: true });
  },

  openUserDropdown: () => {
    get().closeAllModals();
    set({ isUserDropdownOpen: true });
  },

  openTeamDropdown: () => {
    get().closeAllModals();
    set({ isTeamDropdownOpen: true });
  },

  openNavDropdown: () => {
    get().closeAllModals();
    set({ isNavDropdownOpen: true });
  },

  openAboutModal: () => {
    get().closeAllModals();
    set({ showAboutModal: true });
  },

  openCreateTemplate: () => {
    get().closeAllModals();
    set({ showCreateTemplate: true });
  },

  openEditTemplate: (template: TemplateTask) => {
    get().closeAllModals();
    set({ editingTemplate: template });
  },

  openStepsModal: () => {
    get().closeAllModals();
    set({ showStepsModal: true });
  },

  openTemplateSteps: () => {
    // Don't close all modals - we want to keep the template form open
    // get().closeAllModals();
    set({ showTemplateSteps: true });
  },

  openCreateLayer: () => {
    get().closeAllModals();
    set({ showCreateLayer: true });
  },

  openEditLayer: (layer: ArcgisLayerConfig) => {
    // Set editingLayer directly along with closing other modals in a single set() call
    // This avoids the race condition where closeAllModals() sets editingLayer to null first
    set({
      showCreateTask: false,
      editingTask: null,
      viewingTask: null,
      showGeometrySelection: false,
      isCalendarOpen: false,
      isUserDropdownOpen: false,
      isTeamDropdownOpen: false,
      isNavDropdownOpen: false,
      showAboutModal: false,
      showCreateTemplate: false,
      editingTemplate: null,
      showStepsModal: false,
      showTemplateSteps: false,
      showCreateLayer: false,
      editingLayer: layer,
    });
  },

  // Close individual modals
  closeCreateTask: () => set({ showCreateTask: false }),
  closeEditTask: () => set({ editingTask: null }),
  closeViewTask: () => set({ viewingTask: null }),
  closeGeometrySelection: () => set({ showGeometrySelection: false }),
  closeCalendar: () => set({ isCalendarOpen: false }),
  closeUserDropdown: () => set({ isUserDropdownOpen: false }),
  closeTeamDropdown: () => set({ isTeamDropdownOpen: false }),
  closeNavDropdown: () => set({ isNavDropdownOpen: false }),
  closeAboutModal: () => set({ showAboutModal: false }),
  closeCreateTemplate: () => set({ showCreateTemplate: false }),
  closeEditTemplate: () => set({ editingTemplate: null }),
  closeStepsModal: () => set({ showStepsModal: false }),
  closeTemplateSteps: () => set({ showTemplateSteps: false }),
  closeCreateLayer: () => set({ showCreateLayer: false }),
  closeEditLayer: () => set({ editingLayer: null }),

  // Toggle actions for dropdowns
  toggleCalendar: () => {
    const { isCalendarOpen } = get();
    if (isCalendarOpen) {
      set({ isCalendarOpen: false });
    } else {
      get().openCalendar();
    }
  },

  toggleUserDropdown: () => {
    const { isUserDropdownOpen } = get();
    if (isUserDropdownOpen) {
      set({ isUserDropdownOpen: false });
    } else {
      get().openUserDropdown();
    }
  },

  toggleTeamDropdown: () => {
    const { isTeamDropdownOpen } = get();
    if (isTeamDropdownOpen) {
      set({ isTeamDropdownOpen: false });
    } else {
      get().openTeamDropdown();
    }
  },

  toggleNavDropdown: () => {
    const { isNavDropdownOpen } = get();
    if (isNavDropdownOpen) {
      set({ isNavDropdownOpen: false });
    } else {
      get().openNavDropdown();
    }
  },
}));
